import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { client } from '../../api/client';

import cookie from '../../../storage/cookie';
import logger from '../../../utils/logger';
import recaptcha from '../../../utils/recaptcha';

export const Status = Object.freeze({
	Uninitialised: 'uninitialised',
	Loading: 'loading',
	Succeeded: 'succeeded',
	Failed: 'failed'
});

export const PanelNames = Object.freeze({
	Reviews: 'reviews',
	Specs: 'specs'
});

const initialState = {
	filterStatus: Status.Uninitialised,
	visiblePanel: PanelNames.Reviews,
	recaptchaKey: null,
	reviewFilterState: {
		resultsUrl: null,
		manufacturerItems: [],
		navigatedManufacturer: null,
		rangeItems: [],
		navigatedRange: null,
		groupedModelItems: [],
		navigatedModel: null
	},
	specsVrmState: {
		errorMessage: null,
		confirmationPageUrl: null
	}
};

const makeApiData = (state, filterName, filterValue) => {
	const reviewFilterState = state.reviewFilterState;

	const result = {
		manufacturer: reviewFilterState.navigatedManufacturer?.value,
		range: reviewFilterState.navigatedRange?.value,
		model: reviewFilterState.navigatedModel?.value,
	};

	if (filterName) {
		result[filterName] = filterValue;
	}

	return result;
};

export const fetchUpdatedReviewFilterState = createAsyncThunk(
	'homeReviewSpecs/fetchUpdatedReviewFilterState',
	async (filter, { getState }) => {
		const { homeReviewSpecs } = getState();

		const apiData = makeApiData(homeReviewSpecs, filter.name, filter.value);

		const response = await client.post(
			'/api/taxonomy-search/review-filters/',
			apiData
		);

		return {
			reviewFilterState: response.data
		};
	}
);

export const fetchInitialState = createAsyncThunk(
	'homeReviewSpecs/fetchInitialState',
	async (recaptchaKey, { getState }) => {
		const { homeReviewSpecs } = getState();
		const apiData = makeApiData(homeReviewSpecs);

		const response = await client.post(
			'/api/taxonomy-search/review-filters/',
			apiData
		);

		return {
			recaptchaKey: recaptchaKey,
			reviewFilterState: response.data
		};
	},
	{
		condition: (recaptchaKey, { getState }) => {
			const { homeReviewSpecs } = getState();

			if (homeReviewSpecs.filterStatus !== Status.Uninitialised) {
				return false;
			}
		}
	}
);

export const submitVrm = createAsyncThunk(
	'homeReviewSpecs/submitVrm',
	async (vrm, { getState }) => {
		const { homeReviewSpecs } = getState();

		const recaptchaSiteKey = homeReviewSpecs.recaptchaKey;
		let recaptchaToken;

		try {
			recaptchaToken = await recaptcha.v3ExecuteAsync(recaptchaSiteKey, 'submit');
		} catch (error) {
			logger.error(error);
		}

		let errorMessage = null;
		let confirmationPageUrl = null;

		if (recaptchaToken) {
			const response = await client.post(
				'/api/cars/vrm/',
				{
					vrm: vrm,
					pathSegment: 'car-specs',
					recaptchaResponse: recaptchaToken,
					isVersion3: true,
					storeIfSuccessful: true
				}
			);

			errorMessage = response.data.errorMessage;

			if (response.data.isSuccess) {
				cookie.setCookie(response.data.vrmCookieName, response.data.vrm);
				confirmationPageUrl = response.data.confirmPageUrl;
			}
		} else {
			errorMessage = 'An error occured, please try again or search by make and model';
		}

		return {
			specsVrmState: {
				errorMessage: errorMessage,
				confirmationPageUrl: confirmationPageUrl
			}
		};
	}
);

export const homeReviewSpecsSlice = createSlice({
	name: 'homeReviewSpecs',
	initialState,
	reducers: {
		setVisiblePanel(state, action) {
			state.visiblePanel = action.payload;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUpdatedReviewFilterState.pending, (state) => {
				state.filterStatus = Status.Loading;
			})
			.addCase(fetchUpdatedReviewFilterState.fulfilled, (state, action) => {
				const { reviewFilterState } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.reviewFilterState = reviewFilterState;
			})
			.addCase(fetchUpdatedReviewFilterState.rejected, (state) => {
				state.filterStatus = Status.Failed;
			});

		builder
			.addCase(fetchInitialState.pending, (state) => {
				state.filterStatus = Status.Loading;
			})
			.addCase(fetchInitialState.fulfilled, (state, action) => {
				const { recaptchaKey, reviewFilterState } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.recaptchaKey = recaptchaKey;
				state.reviewFilterState = reviewFilterState;
			})
			.addCase(fetchInitialState.rejected, (state) => {
				state.filterStatus = Status.Failed;
			});

		builder
			.addCase(submitVrm.pending, (state) => {
				state.filterStatus = Status.Loading;
				state.specsVrmState.errorMessage = null;
			})
			.addCase(submitVrm.fulfilled, (state, action) => {
				const { specsVrmState } = action.payload;
				state.filterStatus = Status.Succeeded;
				state.specsVrmState = specsVrmState;
			})
			.addCase(submitVrm.rejected, (state) => {
				state.filterStatus = Status.Failed;
				state.specsVrmState.errorMessage = 'An error occured, please try again';
			});
	}
});

export const { setVisiblePanel } = homeReviewSpecsSlice.actions;

export default homeReviewSpecsSlice.reducer;